// Angular Imports
import {inject, Injectable} from '@angular/core'
import {DomSanitizer} from '@angular/platform-browser'
// Shared Imports
import {FilePreview} from '@shared/models'

@Injectable({
	providedIn: 'root',
})
export class ToolsService {
	readonly #domSanitizer = inject(DomSanitizer)

	convertToBlob({ binaryData, contentType }: ConvertToBlob): Blob {
		return new Blob([binaryData], { type: contentType })
	}

	buildFilePreview(file: File): Promise<FilePreview> {
		return new Promise((resolve) => {
			const reader = new FileReader()
			reader.readAsDataURL(file)
			reader.onload = () => {
				const unsafeImage = window.URL.createObjectURL(file)
				const safeImage = this.#domSanitizer.bypassSecurityTrustUrl(unsafeImage)

				resolve({
					file: file,
					preview: safeImage,
				})
			}
			reader.onerror = (error) => {
				resolve({
					file: file,
					preview: null,
				})
			}
		})
	}

	downloadFile({ filename, binaryData }: DownloadFile) {
		let contentType = ''
		const format = filename.split('.').at(-1)
		switch (format) {
			case 'xlsx':
				contentType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
				break
		}

		const blob = this.convertToBlob({ binaryData, contentType })
		const downloadUrl = URL.createObjectURL(blob)
		const downloadLink = document.createElement('a')
		downloadLink.href = downloadUrl
		downloadLink.download = filename
		downloadLink.click()
		URL.revokeObjectURL(downloadUrl)
	}

	// This function, generateNumbers, takes in an object of type IGenerateNumbers as an argument and returns an array of numbers based on the provided starting point, step value, and maximum value.
	generateNumbers({ start, step, maximun }: IGenerateNumbers): Array<number> {
		const numbers: Array<number> = []
		const stepValidated = step ? step : 1
		const startValidated = start ? start : stepValidated

		for (let index = startValidated; index <= maximun; index += stepValidated) {
			numbers.push(index)
		}

		return numbers
	}

	// This function, capitalizeLetters, takes in a string as an argument and returns the same string with the first letter of each word capitalized.
	capitalizeLetters(phrase: string): string {
		const words = phrase.split(' ')
		const wordCapitalized = words.map((word: string) => {
			const firstLetter = word.charAt(0).toUpperCase()
			const othersLetters = word.slice(1)
			return firstLetter + othersLetters
		})
		return wordCapitalized.join(' ')
	}

	// Verify if value is number
	verifyIfValueIsNumber(value: any): boolean {
		return typeof value === 'number' ? true : false
	}
}

interface ConvertToBlob {
	binaryData: ArrayBuffer | string
	contentType: string
}

interface DownloadFile {
	filename: string
	binaryData: ArrayBuffer | string
}

interface IGenerateNumbers {
	start?: number
	step?: number
	maximun: number
}
